import React, { useEffect, useState } from "react";
import Logo from "../assets/logo.png";

// Define the BeforeInstallPromptEvent type
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

const App: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);
  const [confetti, setConfetti] = useState(false);

  // Detect if iOS
  const isIos = () => /iPad|iPhone|iPod/.test(navigator.userAgent);

  useEffect(() => {
    // Listen for Android PWA install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    // Listen for appinstalled event
    const handleAppInstalled = () => setInstalled(true);

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    // Detect if already installed
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as any).standalone === true;
    if (isStandalone) setInstalled(true);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  // Trigger confetti animation
  const triggerConfetti = () => {
    setConfetti(true);
    setTimeout(() => setConfetti(false), 2000);
  };

  // Handle install button click
  const handleInstallClick = () => {
    if (deferredPrompt) {
      // Android: show native install prompt
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then((choiceResult) => {
        if (choiceResult.outcome === "accepted") {
          setInstalled(true);
          triggerConfetti();
        }
      });
    } else if (isIos()) {
      // iOS: show instructions
      alert("iOS users: Tap the Share icon in Safari → Add to Home Screen to install the app.");
    } else {
      // Other unsupported browsers
      alert("Your browser doesn't support install prompts.");
    }
  };

  // Falling diamonds animation
  const generateFallingIcons = () => {
    return [...Array(15)].map((_, i) => {
      const left = Math.random() * 100;
      const duration = 8 + Math.random() * 5;
      const delay = Math.random() * 5;
      const size = 16 + Math.random() * 16;
      return (
        <div
          key={i}
          style={{
            position: "absolute",
            top: "-10%",
            left: `${left}%`,
            fontSize: `${size}px`,
            animation: `fall${i} ${duration}s linear ${delay}s infinite`,
            opacity: 0.8,
            pointerEvents: "none",
          }}
        >
          💎
          <style>
            {`
              @keyframes fall${i} {
                0% { top: -10%; transform: rotate(0deg); opacity: 0.8; }
                100% { top: 110%; transform: rotate(360deg); opacity: 0.8; }
              }
            `}
          </style>
        </div>
      );
    });
  };

  // Confetti animation
  const generateConfetti = () => {
    return [...Array(50)].map((_, i) => {
      const left = Math.random() * 100;
      const color = ["#f94144", "#f3722c", "#f9c74f", "#90be6d", "#577590"][
        Math.floor(Math.random() * 5)
      ];
      const size = 6 + Math.random() * 6;
      return (
        <div
          key={i}
          style={{
            position: "absolute",
            top: 0,
            left: `${left}%`,
            width: `${size}px`,
            height: `${size}px`,
            backgroundColor: color,
            borderRadius: "50%",
            animation: `confettiFall${i} 1.5s ease-out forwards`,
            pointerEvents: "none",
          }}
        >
          <style>
            {`
              @keyframes confettiFall${i} {
                0% { transform: translateY(0) rotate(0deg); opacity:1; }
                100% { transform: translateY(200px) rotate(720deg); opacity:0; }
              }
            `}
          </style>
        </div>
      );
    });
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Segoe UI', Roboto, sans-serif",
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#f2f4f7",
      }}
    >
      {generateFallingIcons()}
      {confetti && generateConfetti()}

      <div
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          padding: "2rem",
          borderRadius: "15px",
          maxWidth: "400px",
          width: "100%",
          textAlign: "center",
          boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
          zIndex: 10,
        }}
      >
        <img
          src={Logo}
          alt="Williams Court Hotel Logo"
          style={{
            width: "70px",
            marginBottom: "1rem",
            display: "block",
            marginLeft: "auto",
            marginRight: "auto",
          }}
        />

        <h1 style={{ marginBottom: "1rem", color: "#0d6efd" }}>
          Welcome to Williams Court Hotel
        </h1>

        <p style={{ marginBottom: "2rem", fontSize: "1rem", color: "#333" }}>
          Install our app for a smoother experience on any device. Book rooms, order services, and check amenities easily.
        </p>

        {!installed && (
          <button
            onClick={handleInstallClick}
            style={{
              width: "100%",
              padding: "1rem",
              fontSize: "1rem",
              backgroundColor: "#0d6efd",
              color: "#fff",
              border: "none",
              borderRadius: "10px",
              cursor: "pointer",
              transition: "0.3s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#084298")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#0d6efd")}
          >
            Install App
          </button>
        )}

        {isIos() && !installed && (
          <p style={{ marginTop: "1.5rem", fontSize: "0.95rem", color: "#555" }}>
            iOS users: Tap the <strong>Share</strong> icon in Safari → <strong>Add to Home Screen</strong>.
          </p>
        )}
      </div>
    </div>
  );
};

export default App;
